import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

// Validated categorical slots from the dataviz palette (slot order fixed,
// never cycled) -- reused as single-series accents where only one color is
// needed, and as the blue/orange pair (slots 1+2, validated adjacent CVD
// pair) for the two-series Gross/Net chart.
const CHART_COLORS = {
  points: "#2a78d6",   // slot 1, blue
  handicap: "#1baf7a", // slot 3, aqua
  gross: "#eb6834",    // slot 2, orange
  net: "#2a78d6",      // slot 1, blue
  rank: "#4a3aa7",     // slot 7, violet
};

// archived_events.date is a text column with inconsistent formats (plain
// 'YYYY-MM-DD' vs ISO-with-time) but always starts with a consistent
// YYYY-MM-DD prefix, so slicing to 10 chars before parsing is reliable
// regardless of which format a given row used.
const formatEventDate = (d) => {
  const iso = String(d || "").slice(0, 10);
  const dt = new Date(iso + "T00:00:00");
  if (isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

// Shared between the on-screen Player Stats drill-down modal and the
// standalone /print/player-stats/:name page rendered server-side by
// Puppeteer for PDF export -- one implementation, so the exported PDF can
// never visually drift from what's shown on screen.
export default function PlayerSeasonCharts({ playerName, rows }) {
  const chartData = rows.map(r => ({
    label: formatEventDate(r.date),
    points: r.points,
    handicap: r.handicap,
    gross: r.gross,
    net: r.net,
    rank: r.rank,
  }));
  const axisTickStyle = { fontSize: 11, fill: "#898781" };
  const gridProps = { stroke: "#e1e0d9", vertical: false };
  const tooltipCursor = { stroke: "#c3c2b7", strokeDasharray: "3 3" };
  const dotProps = (color) => ({ r: 4, strokeWidth: 2, stroke: "#fff", fill: color });

  return (
    <div style={{ background: "#ffffff", padding: "4px" }}>
      <div style={{ fontSize: "15px", fontWeight: "700", color: "#065f46", marginBottom: "2px" }}>{playerName} — Season Trends</div>
      <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "14px" }}>Every archived round this season, in order played ({rows.length} total). Resets when Clear Stats is used.</div>
      <div style={{ display: "flex", flexWrap: "wrap", marginBottom: "0px" }}>
        <div style={{ width: "50%", boxSizing: "border-box", paddingRight: "7px", paddingBottom: "14px" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>Points per event</div>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="label" tick={axisTickStyle} axisLine={{ stroke: "#c3c2b7" }} tickLine={false} />
              <YAxis tick={axisTickStyle} axisLine={{ stroke: "#c3c2b7" }} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={tooltipCursor} />
              <Line type="monotone" dataKey="points" name="Points" stroke={CHART_COLORS.points} strokeWidth={2} dot={dotProps(CHART_COLORS.points)} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div style={{ width: "50%", boxSizing: "border-box", paddingLeft: "7px", paddingBottom: "14px" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>Handicap trend</div>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="label" tick={axisTickStyle} axisLine={{ stroke: "#c3c2b7" }} tickLine={false} />
              <YAxis tick={axisTickStyle} axisLine={{ stroke: "#c3c2b7" }} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={tooltipCursor} />
              <Line type="monotone" dataKey="handicap" name="Handicap" stroke={CHART_COLORS.handicap} strokeWidth={2} dot={dotProps(CHART_COLORS.handicap)} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div style={{ width: "50%", boxSizing: "border-box", paddingRight: "7px", paddingBottom: "14px" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>Gross vs Net score</div>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="label" tick={axisTickStyle} axisLine={{ stroke: "#c3c2b7" }} tickLine={false} />
              <YAxis tick={axisTickStyle} axisLine={{ stroke: "#c3c2b7" }} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={tooltipCursor} />
              <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: "11px" }} />
              <Line type="monotone" dataKey="gross" name="Gross" stroke={CHART_COLORS.gross} strokeWidth={2} dot={dotProps(CHART_COLORS.gross)} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="net" name="Net" stroke={CHART_COLORS.net} strokeWidth={2} dot={dotProps(CHART_COLORS.net)} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div style={{ width: "50%", boxSizing: "border-box", paddingLeft: "7px", paddingBottom: "14px" }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>Rank / position <span style={{ fontWeight: 400, color: "#9ca3af" }}>(rising = improving)</span></div>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid {...gridProps} />
              <XAxis dataKey="label" tick={axisTickStyle} axisLine={{ stroke: "#c3c2b7" }} tickLine={false} />
              <YAxis tick={axisTickStyle} axisLine={{ stroke: "#c3c2b7" }} tickLine={false} allowDecimals={false} reversed domain={[1, "dataMax"]} />
              <Tooltip cursor={tooltipCursor} />
              <Line type="monotone" dataKey="rank" name="Rank" stroke={CHART_COLORS.rank} strokeWidth={2} dot={dotProps(CHART_COLORS.rank)} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div style={{ fontSize: "11px", fontWeight: "700", color: "#374151", marginBottom: "6px" }}>Table view — {rows.length} round{rows.length === 1 ? "" : "s"}</div>
      <table className="w-full text-xs border-collapse">
        <thead className="bg-green-800 text-white">
          <tr>
            <th className="px-2 py-0.5 text-left">Date</th>
            <th className="px-2 py-0.5 text-left">Format</th>
            <th className="px-2 py-0.5 text-center">Handicap</th>
            <th className="px-2 py-0.5 text-center">Gross</th>
            <th className="px-2 py-0.5 text-center">Net</th>
            <th className="px-2 py-0.5 text-center">Points</th>
            <th className="px-2 py-0.5 text-center">Rank</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={"border-b border-gray-200 " + (i % 2 === 0 ? "bg-white" : "bg-green-50")}>
              <td className="px-2 py-0.5">{formatEventDate(r.date)}</td>
              <td className="px-2 py-0.5">{r.game_type}</td>
              <td className="px-2 py-0.5 text-center">{r.handicap}</td>
              <td className="px-2 py-0.5 text-center">{r.gross}</td>
              <td className="px-2 py-0.5 text-center">{r.net}</td>
              <td className="px-2 py-0.5 text-center">{r.points}</td>
              <td className="px-2 py-0.5 text-center">{r.rank}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
